import { useEffect, useRef, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";
import { PanelLeftClose, ChevronLeft } from "lucide-react";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";
import { Button } from "@/design-system/components/Button/Button";
import useAccountMetadata from "@/hooks/useAccountMetadata";

import { NavScroll, NavScrollProvider } from "./navScrollContext";
import NavBarSkeleton from "./NavBarSkeleton";
import { renderNavItems } from "./renderingUtils";
import { getOrganizationSettingsNavItems } from "./navigationItems";
import { animateScrollTop, restoreScrollPosition } from "./scrollUtils";
import { NavBarProps } from "./types";

interface SettingsBarProps extends NavBarProps {
  backToAppPath?: string;
}

export const SettingsBar = ({
  className,
  drawerCollapse,
  setDrawerCollapse,
  backToAppPath = "/",
  ...rest
}: SettingsBarProps) => {
  const history = useHistory();
  const location = useLocation();
  const accountMetadata = useAccountMetadata();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    restoreScrollPosition(scrollContainerRef);
  }, []);

  const cancelNavScrollRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelNavScrollRef.current?.(), []);

  const navScroll = useMemo<NavScroll>(
    () => ({
      containerRef: scrollContainerRef,
      scrollTo: (targetTop: number) => {
        if (!scrollContainerRef.current) {
          return;
        }
        cancelNavScrollRef.current?.();
        cancelNavScrollRef.current = animateScrollTop(
          scrollContainerRef.current,
          targetTop,
        );
      },
    }),
    [],
  );
  const organizationSettingsNavItems = getOrganizationSettingsNavItems();
  const accountMetadataLoaded = accountMetadata.loaded ?? false;

  return (
    <div className="flex h-full flex-col">
      <div
        className={clsx(
          "bg-nav-shell flex h-[68px] items-center justify-between transition-[padding] duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
          drawerCollapse ? "px-3" : "px-4",
        )}
      >
        <Button
          className={clsx(
            "bg-nav-hover hover:!bg-nav-selected rounded-full",
            drawerCollapse && "px-2",
          )}
          leadingIcon={<ChevronLeft />}
          onClick={() => history.push(backToAppPath)}
          tooltipContent={drawerCollapse && "Back to app"}
        >
          {!drawerCollapse && "Back to app"}
        </Button>
        <Tooltip content="Collapse navigation" triggerProps={{ asChild: true }}>
          <button
            type="button"
            onClick={() => setDrawerCollapse(true)}
            aria-label="Collapse navigation"
            aria-hidden={drawerCollapse}
            tabIndex={drawerCollapse ? -1 : 0}
            className={clsx(
              "hover:bg-nav-hover flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-550 ease-[cubic-bezier(0.16,1,0.3,1)]",
              drawerCollapse && "w-0 overflow-hidden opacity-0",
            )}
          >
            <PanelLeftClose size={18} />
          </button>
        </Tooltip>
      </div>
      <div className="bg-nav-shell border-b border-white/15" />
      <nav
        {...rest}
        className={clsx(
          "scrollbar bg-nav-shell flex flex-1 flex-col overflow-y-auto p-1.5 pb-1",
          className,
        )}
        ref={scrollContainerRef}
      >
        {accountMetadataLoaded ? (
          <NavScrollProvider value={navScroll}>
            {renderNavItems({
              collapsed: drawerCollapse,
              items: organizationSettingsNavItems,
              pathname: location.pathname,
            })}
          </NavScrollProvider>
        ) : (
          <NavBarSkeleton />
        )}
      </nav>
    </div>
  );
};
